import {
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon
} from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
  IconButton,
  Chip
} from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'

import { useAnalytics } from '../../contexts/AnalyticsContext'
import { AdminService } from '../../services/AdminService'
import { AnalyticsEvents } from '../../types/AnalyticsEvents'
import Database from '../../types/Database'
import { getRoleColor } from '../../utils/rolePermissions'

import ConfirmationDialog from './ConfirmationDialog'

/**
 * UserManagement Component
 *
 * Displays user list grouped by company (email domain) with search, sorting,
 * and management operations. Shows offices and roles for each user.
 * Allows administrators to promote/demote users and delete accounts.
 *
 * Requirements: 3.1, 3.2, 3.7, 6.1, 6.2, 6.3, 6.4, 6.5, 6.6, 6.7, 8.1, 8.2, 8.3, 8.4, 9.2, 9.4
 */

export interface UserManagementProps {
  currentUserId: string
  currentUserEmail: string
  onError?: (error: string) => void
  onSuccess?: (message: string) => void
}

type SortField = 'email' | 'name' | 'createdAt' | 'isAdmin'
type SortDirection = 'asc' | 'desc'

interface CompanyGroup {
  domain: string
  userCount: number
}

interface UserOfficeRole {
  officeName: string
  role: 'owner' | 'manager' | 'member'
}

const getRoleDisplayLabel = (role: 'owner' | 'manager' | 'member'): string => {
  switch (role) {
    case 'owner':
      return 'Owner'
    case 'manager':
      return 'Manager'
    case 'member':
      return 'User'
    default:
      return 'User'
  }
}

const UserManagement: React.FC<UserManagementProps> = ({
  currentUserId,
  currentUserEmail,
  onError,
  onSuccess
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { trackEvent } = useAnalytics()

  const [users, setUsers] = useState<Database.User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<Database.User[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<SortField>('email')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [isLoading, setIsLoading] = useState(true)
  const [isOperating, setIsOperating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Company grouping state
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null)
  const [companies, setCompanies] = useState<CompanyGroup[]>([])
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyGroup[]>([])

  // Office roles per user
  const [userOfficeRoles, setUserOfficeRoles] = useState<
    Record<string, UserOfficeRole[]>
  >({})
  const [loadingOfficeRoles, setLoadingOfficeRoles] = useState(false)

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    message: string
    onConfirm: () => void
    severity: 'warning' | 'error' | 'info'
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning'
  })

  useEffect(() => {
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (selectedCompany) {
      filterAndSortUsers()
    } else {
      filterCompanies()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchQuery, sortBy, sortDirection, selectedCompany])

  const getDomain = (email: string | null): string => {
    if (!email || !email.includes('@')) return 'Unknown'
    return email.split('@')[1]
  }

  const buildCompanyGroups = useCallback(
    (userList: Database.User[]): CompanyGroup[] => {
      const domainMap: Record<string, number> = {}
      userList.forEach(user => {
        const domain = getDomain(user.email)
        domainMap[domain] = (domainMap[domain] || 0) + 1
      })
      return Object.entries(domainMap)
        .map(([domain, userCount]) => ({ domain, userCount }))
        .sort((a, b) => b.userCount - a.userCount)
    },
    []
  )

  useEffect(() => {
    const groups = buildCompanyGroups(users)
    setCompanies(groups)
  }, [users, buildCompanyGroups])

  const filterCompanies = () => {
    if (!searchQuery.trim()) {
      setFilteredCompanies(companies)
      return
    }
    const query = searchQuery.toLowerCase()
    setFilteredCompanies(
      companies.filter(c => c.domain.toLowerCase().includes(query))
    )
  }

  const loadUsers = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const allUsers = await AdminService.getAllUsers()
      setUsers(allUsers)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load users'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const loadOfficeRolesForUsers = async (userList: Database.User[]) => {
    setLoadingOfficeRoles(true)
    const roles: Record<string, UserOfficeRole[]> = {}
    try {
      const promises = userList.map(async user => {
        if (user.uid) {
          const officeRoles =
            await AdminService.getUserOfficesWithRoles(user)
          roles[user.uid] = officeRoles
        }
      })
      await Promise.all(promises)
      setUserOfficeRoles(prev => ({ ...prev, ...roles }))
    } catch {
      // Silently handle - office roles are supplementary info
    } finally {
      setLoadingOfficeRoles(false)
    }
  }

  const handleCompanyClick = (domain: string) => {
    setSelectedCompany(domain)
    setSearchQuery('')
    // Load office roles for users in this company
    const companyUsers = users.filter(u => getDomain(u.email) === domain)
    loadOfficeRolesForUsers(companyUsers)
  }

  const handleBackToCompanies = () => {
    setSelectedCompany(null)
    setSearchQuery('')
  }

  const filterAndSortUsers = () => {
    let result = users.filter(u => getDomain(u.email) === selectedCompany)

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(user => {
        const email = user.email?.toLowerCase() || ''
        const name = `${user.firstname || ''} ${
          user.lastname || ''
        }`.toLowerCase()
        return email.includes(query) || name.includes(query)
      })
    }

    // Apply sorting
    result.sort((a, b) => {
      let compareValue = 0

      switch (sortBy) {
        case 'email':
          compareValue = (a.email || '').localeCompare(b.email || '')
          break
        case 'name':
          // eslint-disable-next-line no-case-declarations
          const nameA = `${a.firstname || ''} ${a.lastname || ''}`.trim()
          // eslint-disable-next-line no-case-declarations
          const nameB = `${b.firstname || ''} ${b.lastname || ''}`.trim()
          compareValue = nameA.localeCompare(nameB)
          break
        case 'createdAt':
          // eslint-disable-next-line no-case-declarations
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
          // eslint-disable-next-line no-case-declarations
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0
          compareValue = dateA - dateB
          break
        case 'isAdmin':
          compareValue = (a.isAdmin ? 1 : 0) - (b.isAdmin ? 1 : 0)
          break
      }

      return sortDirection === 'asc' ? compareValue : -compareValue
    })

    setFilteredUsers(result)
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortDirection('asc')
    }
  }

  const handleRoleChange = (user: Database.User) => {
    const newRole = !user.isAdmin
    const action = newRole ? 'promote' : 'demote'

    setConfirmDialog({
      open: true,
      title: `${action === 'promote' ? 'Promote' : 'Demote'} User`,
      message: `Are you sure you want to ${action} ${user.email} ${
        newRole ? 'to administrator' : 'from administrator'
      }?`,
      onConfirm: () => confirmRoleChange(user, newRole),
      severity: 'warning'
    })
  }

  const confirmRoleChange = async (user: Database.User, newRole: boolean) => {
    setConfirmDialog({ ...confirmDialog, open: false })
    setIsOperating(true)

    try {
      if (!user.uid) {
        throw new Error('User ID is missing')
      }

      await AdminService.updateUserRole(user.uid, newRole)

      // Log the action
      await AdminService.logAdminAction(
        currentUserId,
        currentUserEmail,
        newRole ? 'user_promoted' : 'user_demoted',
        user.uid,
        user.email || undefined,
        {}
      )

      // Track analytics event
      try {
        trackEvent(AnalyticsEvents.ADMIN_USER_MANAGEMENT, {
          action_type: newRole ? 'promote_user' : 'demote_user'
        })
      } catch (error) {
        console.error('Failed to track admin user management event:', error)
      }

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.uid === user.uid ? { ...u, isAdmin: newRole } : u
        )
      )

      const message = `Successfully ${newRole ? 'promoted' : 'demoted'} ${
        user.email
      }`
      setSuccessMessage(message)
      if (onSuccess) onSuccess(message)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update user role'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsOperating(false)
    }
  }

  const handleDeleteUser = (user: Database.User) => {
    setConfirmDialog({
      open: true,
      title: 'Delete User',
      message: `Are you sure you want to delete ${user.email}? This will remove the user from all offices and delete all their bookings. This action cannot be undone.`,
      onConfirm: () => confirmDeleteUser(user),
      severity: 'error'
    })
  }

  const confirmDeleteUser = async (user: Database.User) => {
    setConfirmDialog({ ...confirmDialog, open: false })
    setIsOperating(true)

    try {
      if (!user.uid) {
        throw new Error('User ID is missing')
      }

      await AdminService.deleteUser(user.uid)

      // Log the action
      await AdminService.logAdminAction(
        currentUserId,
        currentUserEmail,
        'user_deleted',
        user.uid,
        user.email || undefined,
        {}
      )

      // Track analytics event
      try {
        trackEvent(AnalyticsEvents.ADMIN_USER_MANAGEMENT, {
          action_type: 'delete_user'
        })
      } catch (error) {
        console.error('Failed to track admin user management event:', error)
      }

      // Remove from local state
      setUsers(prevUsers => prevUsers.filter(u => u.uid !== user.uid))

      const message = `Successfully deleted user ${user.email}`
      setSuccessMessage(message)
      if (onSuccess) onSuccess(message)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete user'
      setError(errorMessage)
      if (onError) onError(errorMessage)
    } finally {
      setIsOperating(false)
    }
  }

  const formatDate = (date: Date | undefined): string => {
    if (!date) return 'N/A'
    const d = date instanceof Date ? date : new Date(date)
    return d.toLocaleDateString()
  }

  const getUserName = (user: Database.User): string => {
    const name = `${user.firstname || ''} ${user.lastname || ''}`.trim()
    return name || 'N/A'
  }

  const isCurrentUser = (user: Database.User): boolean => {
    return user.uid === currentUserId
  }

  const renderOfficeRoles = (user: Database.User) => {
    if (!user.uid) return null
    const roles = userOfficeRoles[user.uid]
    if (!roles || roles.length === 0) {
      if (loadingOfficeRoles) {
        return <CircularProgress size={14} />
      }
      return (
        <Typography variant='caption' color='textSecondary'>
          No offices
        </Typography>
      )
    }
    return (
      <Box display='flex' flexWrap='wrap' gap={0.5}>
        {roles.map((officeRole, idx) => (
          <Chip
            key={idx}
            label={`${officeRole.officeName} (${getRoleDisplayLabel(officeRole.role)})`}
            size='small'
            color={getRoleColor(officeRole.role)}
            variant='outlined'
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        ))}
      </Box>
    )
  }

  // Company List View
  const renderCompanyList = () => {
    const displayCompanies = searchQuery.trim()
      ? filteredCompanies
      : companies

    if (isMobile) {
      return (
        <Box>
          {displayCompanies.length === 0 ? (
            <Typography color='textSecondary' align='center' sx={{ py: 4 }}>
              No companies found
            </Typography>
          ) : (
            displayCompanies.map(company => (
              <Card
                key={company.domain}
                sx={{ mb: 2, cursor: 'pointer' }}
                onClick={() => handleCompanyClick(company.domain)}
              >
                <CardContent>
                  <Box display='flex' alignItems='center' gap={1}>
                    <BusinessIcon color='action' />
                    <Box flex={1}>
                      <Typography variant='h6'>{company.domain}</Typography>
                      <Typography variant='body2' color='textSecondary'>
                        {company.userCount}{' '}
                        {company.userCount === 1 ? 'user' : 'users'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )
    }

    return (
      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Company (Domain)</TableCell>
              <TableCell align='right'>Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayCompanies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  <Typography color='textSecondary' sx={{ py: 4 }}>
                    No companies found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayCompanies.map(company => (
                <TableRow
                  key={company.domain}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleCompanyClick(company.domain)}
                >
                  <TableCell>
                    <Box display='flex' alignItems='center' gap={1}>
                      <BusinessIcon color='action' fontSize='small' />
                      <Typography>{company.domain}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align='right'>
                    <Chip
                      label={`${company.userCount} ${company.userCount === 1 ? 'user' : 'users'}`}
                      size='small'
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  // User List View (within a company)
  const renderUserList = () => {
    if (isMobile) {
      return (
        <Box>
          {filteredUsers.length === 0 ? (
            <Typography color='textSecondary' align='center' sx={{ py: 4 }}>
              No users found
            </Typography>
          ) : (
            filteredUsers.map(user => (
              <Card key={user.uid} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='flex-start'
                    mb={1}
                  >
                    <Box flex={1}>
                      <Typography variant='h6'>{user.email}</Typography>
                      <Typography variant='body2' color='textSecondary'>
                        {getUserName(user)}
                      </Typography>
                      <Typography variant='caption' color='textSecondary'>
                        Created: {formatDate(user.createdAt)}
                      </Typography>
                    </Box>
                    {user.isAdmin && (
                      <Chip
                        icon={<AdminIcon />}
                        label='Admin'
                        color='primary'
                        size='small'
                      />
                    )}
                  </Box>
                  <Box mt={1} mb={1}>
                    {renderOfficeRoles(user)}
                  </Box>
                  <Box display='flex' gap={1} mt={2}>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => handleRoleChange(user)}
                      disabled={isCurrentUser(user) || isOperating}
                      fullWidth
                    >
                      {user.isAdmin ? 'Demote' : 'Promote'}
                    </Button>
                    <Button
                      variant='outlined'
                      color='error'
                      size='small'
                      onClick={() => handleDeleteUser(user)}
                      disabled={isCurrentUser(user) || isOperating}
                      fullWidth
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )
    }

    return (
      <TableContainer component={Card}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortDirection : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>Offices</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'isAdmin'}
                  direction={sortBy === 'isAdmin' ? sortDirection : 'asc'}
                  onClick={() => handleSort('isAdmin')}
                >
                  Admin Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'createdAt'}
                  direction={sortBy === 'createdAt' ? sortDirection : 'asc'}
                  onClick={() => handleSort('createdAt')}
                >
                  Created Date
                </TableSortLabel>
              </TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <Typography color='textSecondary' sx={{ py: 4 }}>
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map(user => (
                <TableRow key={user.uid}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getUserName(user)}</TableCell>
                  <TableCell>{renderOfficeRoles(user)}</TableCell>
                  <TableCell>
                    {user.isAdmin ? (
                      <Chip
                        icon={<AdminIcon />}
                        label='Admin'
                        color='primary'
                        size='small'
                      />
                    ) : (
                      <Chip label='User' size='small' />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell align='right'>
                    <Button
                      variant='outlined'
                      size='small'
                      onClick={() => handleRoleChange(user)}
                      disabled={isCurrentUser(user) || isOperating}
                      sx={{ mr: 1 }}
                    >
                      {user.isAdmin ? 'Demote' : 'Promote'}
                    </Button>
                    <IconButton
                      color='error'
                      size='small'
                      onClick={() => handleDeleteUser(user)}
                      disabled={isCurrentUser(user) || isOperating}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  if (isLoading) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight={400}
      >
        <CircularProgress size={60} />
      </Box>
    )
  }

  if (error && users.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error' onClose={() => loadUsers()}>
          {error}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        User Management
      </Typography>

      {/* Back button when viewing company users */}
      {selectedCompany && (
        <Box display='flex' alignItems='center' gap={1} mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToCompanies}
            variant='outlined'
            size='small'
          >
            Back to Companies
          </Button>
          <Typography variant='h6' color='textSecondary'>
            {selectedCompany}
          </Typography>
        </Box>
      )}

      {/* Search Field */}
      <TextField
        fullWidth
        variant='outlined'
        placeholder={
          selectedCompany
            ? 'Search by email or name...'
            : 'Search by company domain...'
        }
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* Content */}
      {selectedCompany ? renderUserList() : renderCompanyList()}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        severity={confirmDialog.severity}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity='success'
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error && users.length > 0}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setError(null)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default UserManagement
